import { useEffect, useState } from "react"

import type { AiDraftBadgeState } from "./ai-draft-badge"

// A generation that hasn't landed by now is never going to: the Inngest run was
// cancelled, crashed outside its own failure handler, or the workspace ran out
// of AI budget before it could write anything back. Past this point the pill
// reads as failed so the agent can regenerate instead of watching a spinner.
export const AI_DRAFT_STALE_MS = 10 * 60 * 1000

interface Input {
	startedAt: number
	generatedAt: number
	errorAt: number
	hasDraftHtml: boolean
	composerSource: "ai" | "user" | null
	appliedAiDraftAt: number | null
	now: number
}

export function resolveAiDraftBadgeState({
	startedAt,
	generatedAt,
	errorAt,
	hasDraftHtml,
	composerSource,
	appliedAiDraftAt,
	now,
}: Input): AiDraftBadgeState | null {
	const isGenerating = startedAt > generatedAt
	const isStale = isGenerating && now - startedAt > AI_DRAFT_STALE_MS
	if (isGenerating && !isStale) return "generating"

	if (!hasDraftHtml) {
		const hasError = isStale || (errorAt > 0 && errorAt > generatedAt)
		return hasError ? "error" : null
	}

	// Applied draft is "stale" when a newer one has been generated since.
	// Surface the new one as opt-in rather than silently overwriting.
	const isStaleAppliedDraft =
		composerSource === "ai" &&
		appliedAiDraftAt != null &&
		generatedAt > appliedAiDraftAt
	if (composerSource === "ai" && !isStaleAppliedDraft) return "applied"
	// The agent has typed their own reply, so drop the badge rather than offer
	// to re-apply over their work.
	if (composerSource === "user") return null
	return "available"
}

// Nothing re-renders the composer when a generation simply stops responding, so
// an in-flight draft schedules its own wake-up at the staleness boundary.
export function useAiDraftStaleTick(startedAt: number | null): number {
	const [now, setNow] = useState(() => Date.now())
	useEffect(() => {
		if (startedAt === null) return
		const remaining = startedAt + AI_DRAFT_STALE_MS - Date.now()
		if (remaining <= 0) {
			setNow(Date.now())
			return
		}
		const timer = setTimeout(() => setNow(Date.now()), remaining + 250)
		return () => clearTimeout(timer)
	}, [startedAt])
	return now
}
